import { FormField, fieldA11y } from '@/components/FormField';
import { Icon } from '@/components/Icon';
import { Kicker } from '@/components/Kicker';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import { ProfileAssertions } from './ProfileAssertions';
import type { ProfileFormApi } from './useProfileForm';

const SWITCH_ROW = 'flex min-h-11 items-center justify-between gap-3 rounded-lg border bg-background px-3 py-2 text-[13px]';
const LAUNCH_FORM_PLACEHOLDER = '{"schema":{"type":"object","properties":{"track":{"type":"string","enum":["internal","alpha"]}}}}';

function SectionLegend({ children }: { children: string }) {
  return (
    <legend className="mb-1">
      <Kicker className="text-subtle">{children}</Kicker>
    </legend>
  );
}

/** Size budget and bundle id, the two switches, then the env assertions. */
function VerificationFieldset({ form }: { form: ProfileFormApi }) {
  const { values, patch } = form;
  const sizeError = form.errorFor('sizeBudgetMb', true);
  return (
    <fieldset className="flex flex-col gap-3">
      <SectionLegend>Verification</SectionLegend>
      <div className="grid gap-4 sm:grid-cols-2">
        <FormField id="profile-size-budget" label="Size budget (MB)" error={sizeError}>
          <Input data-testid="profile-size-budget" type="number" min={1} value={values.sizeBudgetMb} onChange={(e) => patch({ sizeBudgetMb: e.target.value })} placeholder="100" className="font-mono sm:text-[13px]" {...fieldA11y('profile-size-budget', sizeError)} />
        </FormField>
        <FormField id="profile-bundle-id" label="Bundle id check" hint="Fails the build when the binary's id differs">
          <Input data-testid="profile-bundle-id" value={values.bundleId} onChange={(e) => patch({ bundleId: e.target.value })} placeholder="com.example.app" className="font-mono sm:text-[13px]" spellCheck={false} />
        </FormField>
      </div>
      <div className="grid gap-3 sm:grid-cols-2">
        <label className={SWITCH_ROW}>
          <span>Version monotonic</span>
          <Switch data-testid="profile-version-monotonic" checked={values.versionMonotonic} onCheckedChange={(v) => patch({ versionMonotonic: v })} />
        </label>
        <label className={SWITCH_ROW}>
          <span>Signing matches the store app</span>
          <Switch data-testid="profile-signing" checked={values.signingCheck} onCheckedChange={(v) => patch({ signingCheck: v })} />
        </label>
      </div>
      <ProfileAssertions form={form} />
    </fieldset>
  );
}

/** The optional JSON Schema of the Run build dialog's extra inputs. */
function LaunchFormField({ form }: { form: ProfileFormApi }) {
  const error = form.errorFor('launchForm', true);
  const hint = 'Extra inputs of the Run build dialog; leave empty to use the platform defaults';
  return (
    <FormField id="profile-launch-form" label="Launch form (JSON Schema, optional)" hint={hint} error={error}>
      <Textarea data-testid="profile-launch-form" value={form.values.launchFormJson} onChange={(e) => form.patch({ launchFormJson: e.target.value })} rows={5} spellCheck={false} className="font-mono sm:text-[13px]" placeholder={LAUNCH_FORM_PLACEHOLDER} {...fieldA11y('profile-launch-form', error)} />
    </FormField>
  );
}

function AdvancedTrigger({ open }: { open: boolean }) {
  return (
    <CollapsibleTrigger asChild>
      <button type="button" data-testid="profile-advanced" className="flex min-h-11 w-full items-center justify-between gap-3 rounded-lg px-3 text-left text-[13px] font-medium text-foreground hover:bg-accent">
        <span>
          Advanced <span className="font-normal text-subtle">· verification, submit defaults, launch form</span>
        </span>
        <Icon name={open ? 'expand_less' : 'expand_more'} size={20} className="text-subtle" />
      </button>
    </CollapsibleTrigger>
  );
}

/** Collapsible "Advanced": verification, submit defaults and the launch form. */
export function ProfileAdvanced({ form }: { form: ProfileFormApi }) {
  return (
    <Collapsible open={form.advanced} onOpenChange={form.setAdvanced} className="rounded-lg border">
      <AdvancedTrigger open={form.advanced} />
      <CollapsibleContent className="flex flex-col gap-5 border-t p-3 sm:p-4">
        <VerificationFieldset form={form} />
        <LaunchFormField form={form} />
      </CollapsibleContent>
    </Collapsible>
  );
}
